import { useState, useEffect } from 'react';
import { Sparkles, Trophy } from 'lucide-react';
import { azureClient } from '../lib/azure-public';

type UserInfoFormProps = {
  quizId: string;
  onSubmit: (email: string, name: string) => void;
  messages: {
    heading: string;
    subheading: string;
    nameLabel: string;
    namePlaceholder: string;
    generateButton: string;
    emailLabel: string;
    emailPlaceholder: string;
    emailSubdetails: string;
    submitButton: string;
    errorNameRequired: string;
    errorEmailRequired: string;
    errorEmailInvalid: string;
  };
};

const adjectives = [
  "Active", "Agile", "Async", "Atomic", "Auto", "Binary", "Bitwise", "Cached", "Clean", "Cloud",
  "Clustered", "Coded", "Cognitive", "Compact", "Connected", "Constant", "Cosmic", "Critical", "Cyber", "Data",
  "Debug", "Decoded", "Deployed", "Digital", "Direct", "Dynamic", "Elastic", "Encrypted", "Epic", "Event",
  "Evolved", "Exact", "Expert", "Fast", "Faultless", "Federated", "Fine", "Firewalled", "Fluid", "Formal",
  "Fractal", "Fresh", "Fullstack", "Fuzzy", "Generic", "Global", "Graphic", "Hardened", "Hashed", "Headless",
  "Heuristic", "Highspeed", "Hyper", "Iconic", "Immutable", "Indexed", "Infinite", "Infrared", "Inline", "Intelligent",
  "Interactive", "Iterative", "Joined", "JSON", "Kernel", "Keyed", "Latent", "Layered", "Lazy", "Lean",
  "Linked", "Live", "Logical", "Lossless", "Lowcode", "Magic", "Managed", "Manual", "Massive", "Mesh",
  "Meta", "Metric", "Micro", "Minimal", "Modular", "Native", "Neural", "Nextgen", "Nimble", "Nocturnal",
  "Nodal", "Noisy", "Nullable", "Object", "Observable", "Offline", "Onchain", "Online", "Open", "Optimal",
  "Parallel", "Partial", "Passive", "Patterned", "Persistent", "Pixel", "Portable", "Primary", "Prime", "Procedural",
  "Profiled", "Programmed", "Prompt", "Proxy", "Pulsed", "Quantum", "Queued", "Rapid", "Reactive", "Realtime",
  "Recursive", "Reduced", "Refined", "Remote", "Rendered", "Resolved", "Responsive", "Restful", "Robust", "Rooted",
  "Runtime", "Safe", "Sampled", "Scalar", "Scoped", "Scripted", "Secure", "Semantic", "Serialized", "Serverless",
  "Sharded", "Sharp", "Signed", "Silent", "Simplified", "Simulated", "Single", "Smart", "Socketed",
  "Spatial", "Stateful", "Static", "Streamed", "Strict", "Synced", "Synthetic", "Systemic", "Tagged", "Templated",
  "Tensor", "Terminal", "Threaded", "Timed", "Tokenized", "Topological", "Tracked", "Typed", "Ultra", "Unified",
  "Unique", "Unrolled", "Updated", "Upgraded", "Upstream", "User", "Valid", "Vector", "Verified",
  "Virtual", "Visible", "Volt", "Warm", "Watched", "Weak", "Webscale", "Weighted", "Wired", "Wireless",
  "Workflow", "Zero", "Zoned", "Zappy", "Abstract", "Adaptive", "Alive", "Ambient", "Amped", "Analog",
  "Artifact", "Assisted", "Autonomous", "Backlit", "Bandwidth", "Bolted", "Buffered", "Bugless", "Cached", "Channel",
  "Compressed", "Concrete", "Configured", "Consensus", "Container", "Coordinated", "Current", "Dataless",
  "Dexter", "Diffused", "Discrete", "Distributed", "Docked", "Durable", "Edgewise", "Elevated", "Emulated", "Enabled",
  "Encrypted", "Ephemeral", "Equalized", "Executed", "Exported", "Extended", "Extreme", "Failproof", "Filtered", "Flagged",
  "Future", "Gated", "Governed", "Hardened", "Hierarchic", "Hosted", "Hot", "Hydrated", "Logged"
];

const nouns = [
  "App", "Byte", "Cache", "Chip", "Cloud", "Code", "Data", "Dev", "Disk", "Font",
  "Frame", "Graph", "Grid", "Hash", "Host", "Icon", "Index", "Input", "Key", "Link",
  "Logic", "Loop", "Macro", "Mark", "Mesh", "Node", "Patch", "Ping", "Pipe",
  "Pixel", "Port", "Query", "Queue", "Rack", "Raid", "Ram", "Role", "Route",
  "Scope", "Script", "Seed", "Shard", "Shell", "Slot", "Stack", "State", "Stylus", "Table",
  "Tag", "Task", "Tech", "Telecom", "Temp", "Thread", "Trace", "Track", "Tree",
  "Type", "Ui", "Url", "User", "Vector", "View", "Vlan", "Widget", "Wiki",
  "Wire", "Zone", "Agent", "Alert", "Api", "Array", "Auth", "Batch", "Blob", "Bot",
  "Branch", "Build", "Bus", "Button", "Class", "Client", "Clone", "Cluster", "Cmd", "Codec",
  "Commit", "Compile", "Config", "Console", "Container", "Cookie", "Core", "Cursor", "Daemon", "Dash",
  "Debug", "Deploy", "Device", "Diff", "Domain", "Dump", "Edge", "Encrypt", "Engine", "Event",
  "Export", "Fiber", "Field", "Flag", "Flow", "Fork", "Function", "Gateway", "Gpu", "Hook",
  "Ide", "Image", "Instance", "Job", "Kernel", "Lambda", "Layer", "Ledger", "Library", "License",
  "Loader", "Log", "Map", "Matrix", "Memory", "Merge", "Metric", "Model", "Module", "Monitor",
  "Network", "Nginx", "Object", "Output", "Packet", "Panel", "Parse", "Payload", "Pipeline", "Plugin",
  "Policy", "Portal", "Process", "Proxy", "Repo", "Request", "Response", "Routine", "Runtime", "Schema",
  "Sdk", "Server", "Service", "Session", "Shard", "Socket", "Source", "Sprint", "Sql", "Ssh",
  "Stream", "Syntax", "System", "Tenant", "Terminal", "Test", "Theme", "Ticket", "Trigger", "Tunnel",
  "Udp", "Update", "Upload", "Vault", "Verify", "Version", "Volume", "Webhook", "Worker", "Workspace",
  "Yaml", "Zip", "Cipher", "Circuit", "Column", "Compute", "Cron", "Cursor", "Dataset", "Directive",
  "Driver", "Firmware", "Garbage", "Handle", "Heap", "Import", "Interrupt", "Jitter", "Json", "Keyframe",
  "Latency", "Lexer", "Literal", "Mutex", "Opcode", "Operand", "Param", "Pivot", "Queueing", "Raster",
  "Reducer", "Render", "Replica", "Resolver", "Semaphore", "Shading", "Sprite", "StackFrame", "Virtual",
  "Watcher", "Websocket", "Widgetize", "Workflow", "Xhr", "Xml", "Yield", "Zbuffer", "ApiKey", "Bandwidth",
  "Bootstrap", "Checksum", "Debounce", "Endpoint", "Failover", "Guid", "Hotfix", "Ingress", "Jsonl", "Keepalive"
];


function generateUsername(): string {
  const adjective = adjectives[Math.floor(Math.random() * adjectives.length)];
  const noun = nouns[Math.floor(Math.random() * nouns.length)];
  return `${adjective} ${noun}`;
}

export default function UserInfoForm({ quizId, onSubmit, messages }: UserInfoFormProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isNameReadonly, setIsNameReadonly] = useState(false);
  //const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [isCheckingName, setIsCheckingName] = useState(false);

  useEffect(() => {
    const checkEmailExists = async () => {
      if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setIsNameReadonly(false);
        return;
      }

      //setIsCheckingEmail(true);
      try {
        const existingSession = await azureClient.sessions.getByEmail(email.trim().toLowerCase(), quizId);

        if (existingSession && existingSession.name) {
          setName(existingSession.name);
          setIsNameReadonly(true);
          setErrors((prev) => ({ ...prev, name: '' }));
        } else {
          setIsNameReadonly(false);
        }
      } catch (error) {
        console.error('Error checking email:', error);
        setIsNameReadonly(false);
      }
      //setIsCheckingEmail(false);
    };

    const timeoutId = setTimeout(checkEmailExists, 500);
    return () => clearTimeout(timeoutId);
  }, [email]);

  useEffect(() => {
    const checkNameExists = async () => {
      if (isNameReadonly || !name.trim()) {
        return;
      }

      setIsCheckingName(true);
      try {
        const exists = await azureClient.sessions.checkName(name.trim(), quizId);

        if (exists) {
          setErrors((prev) => ({ ...prev, name: 'This name is already taken. Please choose another name or generate a username.' }));
        } else {
          setErrors((prev) => ({ ...prev, name: '' }));
        }
      } catch (error) {
        console.error('Error checking name:', error);
      }
      setIsCheckingName(false);
    };

    const timeoutId = setTimeout(checkNameExists, 500);
    return () => clearTimeout(timeoutId);
  }, [name, isNameReadonly]);

  const handleGenerate = () => {
    setName(generateUsername());
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: { [key: string]: string } = {};

    if (!email.trim()) {
      newErrors.email = messages.errorEmailRequired;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = messages.errorEmailInvalid;
    }

    if (!name.trim()) {
      newErrors.name = messages.errorNameRequired;
    }

    if (errors.name) {
      return;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(email, name);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-light/30 to-brand-light/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="mb-6 relative">
            <img
              src="/Untitled design (1).png"
              alt="Solace Banner"
              className="w-full h-auto rounded-lg"
            />
            <div className="absolute inset-0 flex items-center justify-end pr-[5%]">
              <h1 className="text-3xl font-bold w-2/3 text-left pl-4 font-heading leading-tight text-brand-dark">
                {messages.heading}
              </h1>
            </div>
          </div>
          <p className="text-brand-dark/70">{messages.subheading}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-brand-dark mb-2">
              {messages.emailLabel}
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setErrors((prev) => ({ ...prev, email: '' }));
              }}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent outline-none transition text-brand-dark"
              placeholder={messages.emailPlaceholder}
            />
            <p className="mt-2 text-xs text-brand-dark/60">{messages.emailSubdetails}</p>
            {errors.email && (
              <p className="mt-2 text-sm text-red-600">{errors.email}</p>
            )}
          </div>

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-brand-dark mb-2">
              {messages.nameLabel}
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => {
                  if (!isNameReadonly) {
                    setName(e.target.value);
                  }
                }}
                readOnly={isNameReadonly}
                className={`flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent outline-none transition text-brand-dark ${isNameReadonly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                placeholder={messages.namePlaceholder}
              />
              <button
                type="button"
                onClick={handleGenerate}
                disabled={isNameReadonly}
                className={`px-4 py-3 rounded-lg font-medium transition flex items-center gap-2 whitespace-nowrap ${isNameReadonly ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-brand-light hover:bg-brand-light/80 text-brand-dark'}`}
                title={messages.generateButton}
              >
                <Sparkles className="w-4 h-4" />
                <span className="hidden sm:inline">{messages.generateButton}</span>
              </button>
            </div>
            {isCheckingName && !isNameReadonly && (
              <p className="mt-2 text-xs text-blue-600">Checking name availability...</p>
            )}
            {errors.name && (
              <p className="mt-2 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          <p className="text-xs text-brand-dark/60 leading-relaxed">
            By starting the quiz you agree to Solace's{' '}
            <a
              href="https://solace.com/privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-green hover:underline"
            >
              privacy policy
            </a>{' '}
            and to receive the occasional email from Solace containing relevant content that I can{' '}
            <a
              href="https://go.solace.com/UnsubscribePage.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-green hover:underline"
            >
              unsubscribe
            </a>{' '}
            from at anytime.
          </p>

          <button
            type="submit"
            className="w-full bg-brand-green text-brand-dark py-3 rounded-lg font-semibold hover:opacity-90 transition transform active:scale-95"
          >
            {messages.submitButton}
          </button>

          <a
            href={`/${quizId}/leaderboard`}
            className="w-full mt-3 inline-flex items-center justify-center gap-2 px-6 py-3 bg-brand-light text-brand-dark font-semibold rounded-lg hover:bg-brand-light/80 transition transform active:scale-95"
          >
            <Trophy className="w-5 h-5" />
            View Leaderboard
          </a>
        </form>
      </div>
    </div>
  );
}
